import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { TransportBar } from '../components/TransportBar';
import { SettingsPanel } from '../components/SettingsPanel';
import { RhythmPatternGrid } from '../components/RhythmPatternGrid';
import { MeasurePageView, pagerLabel } from '../components/MeasurePageView';
import { useRhythmPlayback } from '../hooks/useRhythmPlayback';
import { useFullscreen } from '../hooks/useFullscreen';
import { useMeasurePagination } from '../hooks/useMeasurePagination';
import { usePresentationMode } from '../../contexts/PresentationModeContext';
import { DEFAULT_NOTE_COLORS } from '../constants';
import {
  applyRhythmFigureToBeat,
  createEmptyRhythmPattern,
  resizeRhythmPattern,
} from '../utils/rhythmPattern';
import type { NoteFigure } from '../types';

const STORAGE_SOUND = 'boom-sound-enabled';

interface Props {
  onBack: () => void;
}

export function RhythmApp({ onBack }: Props) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selectedFigure, setSelectedFigure] = useState<NoteFigure>('negra');
  const [bpm, setBpm] = useState(80);
  const [measureCount, setMeasureCount] = useState(2);
  const [beatsPerMeasure, setBeatsPerMeasure] = useState(4);
  const [soundEnabled, setSoundEnabled] = useState(
    () => localStorage.getItem(STORAGE_SOUND) !== 'false',
  );
  const { ref: appRef, isFullscreen, toggle: toggleFullscreen } = useFullscreen();
  const [pattern, setPattern] = useState(() => createEmptyRhythmPattern(2, 4));

  const accent = DEFAULT_NOTE_COLORS.re;

  const { playing, activeIndex, activeMeasureIndex, play, stop } = useRhythmPlayback(
    pattern,
    bpm,
    soundEnabled,
  );

  const { setPresentation } = usePresentationMode();

  useEffect(() => {
    setPresentation(playing);
    return () => setPresentation(false);
  }, [playing, setPresentation]);

  const paginate = playing;
  const { page, pages, slide, windowStart, visibleIndices } = useMeasurePagination(
    measureCount,
    activeMeasureIndex,
    paginate,
    { visible: 2, shift: 2 },
  );

  const [slideUi, setSlideUi] = useState(slide);
  useEffect(() => setSlideUi(slide), [slide, page]);
  const clearSlide = useCallback(() => setSlideUi('none'), []);

  const displayMeasures = paginate ? visibleIndices : undefined;

  useEffect(() => {
    localStorage.setItem(STORAGE_SOUND, String(soundEnabled));
  }, [soundEnabled]);

  const syncLayout = useCallback((nextBeats: number, nextMeasures: number) => {
    setPattern((prev) => resizeRhythmPattern(prev, nextMeasures, nextBeats));
  }, []);

  const handleApplyFigure = (measureIndex: number, beatIndex: number) => {
    setPattern((prev) => ({
      ...prev,
      measures: prev.measures.map((m, mi) =>
        mi !== measureIndex
          ? m
          : applyRhythmFigureToBeat(m, beatIndex, selectedFigure, prev.beatsPerMeasure),
      ),
    }));
  };

  const handleCellToggle = (
    measureIndex: number,
    beatIndex: number,
    cellIndex: number,
  ) => {
    setPattern((prev) => ({
      ...prev,
      measures: prev.measures.map((m, mi) => {
        if (mi !== measureIndex) return m;
        return {
          slots: m.slots.map((slot, bi) => {
            if (bi !== beatIndex || !slot.active) return slot;
            return {
              ...slot,
              cells: slot.cells.map((c, ci) =>
                ci === cellIndex ? { ...c, hit: !c.hit } : c,
              ),
            };
          }),
        };
      }),
    }));
  };

  const cssVars = useMemo(
    () =>
      ({
        '--accent': accent,
        '--rhythm-accent': accent,
      }) as CSSProperties,
    [accent],
  );

  const grid = (
    <RhythmPatternGrid
      pattern={pattern}
      accentColor={accent}
      activeIndex={activeIndex}
      selectedFigure={selectedFigure}
      visibleMeasures={displayMeasures}
      onCellToggle={handleCellToggle}
      onApplyFigure={handleApplyFigure}
    />
  );

  return (
    <div
      ref={appRef}
      className={`app app--rhythm ${isFullscreen ? 'app--fullscreen' : ''} ${paginate ? 'app--paginated app--presentation' : ''}`}
      style={cssVars}
    >
      <header className="app-header">
        <div>
          <button type="button" className="btn-back" onClick={onBack}>
            ← Inicio
          </button>
          <h1 className="app-title">Práctica de ritmos</h1>
          <p className="app-subtitle">Jefferson School · 1ro básico</p>
        </div>
        {!isFullscreen && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setSettingsOpen(true)}
          >
            ⚙ Configuración
          </button>
        )}
      </header>

      <TransportBar
        bpm={bpm}
        onBpmChange={setBpm}
        figure={selectedFigure}
        onFigureChange={setSelectedFigure}
        playing={playing}
        onPlay={play}
        onStop={stop}
        soundEnabled={soundEnabled}
        onSoundToggle={() => setSoundEnabled((s) => !s)}
        isFullscreen={isFullscreen}
        onFullscreenToggle={toggleFullscreen}
      />

      {!paginate && (
        <p className="hint">
          Toca un <strong>tiempo</strong> para subdividirlo con la figura elegida. Toca cada
          casilla para marcar <strong>sonido</strong> (figura rellena) o silencio.
        </p>
      )}

      {paginate && (
        <p className="hint hint--pager">{pagerLabel(windowStart, measureCount, 2)}</p>
      )}

      {paginate ? (
        <MeasurePageView
          page={page}
          pages={pages}
          slide={slideUi}
          onSlideEnd={clearSlide}
          showPager
          measureCount={measureCount}
          windowStart={windowStart}
        >
          {grid}
        </MeasurePageView>
      ) : (
        grid
      )}

      <SettingsPanel
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        colors={DEFAULT_NOTE_COLORS}
        onColorsChange={() => {}}
        shape="circle"
        onShapeChange={() => {}}
        measureCount={measureCount}
        onMeasureCountChange={(n) => {
          setMeasureCount(n);
          syncLayout(beatsPerMeasure, n);
        }}
        beatsPerMeasure={beatsPerMeasure}
        onBeatsChange={(n) => {
          setBeatsPerMeasure(n);
          syncLayout(n, measureCount);
        }}
        onShowWelcome={() => setSettingsOpen(false)}
        rhythmMode
      />
    </div>
  );
}
